import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import type { MeasureUnit } from "@/lib/measurementConverter";

const STORAGE_KEY = "preferred_measure_unit";
const STORAGE_KEY_USER_SET = "preferred_measure_unit_user_set"; // marks explicit choice
// Which market the current auto value was derived from. Market is not known
// for certain on first render — it firms up once the user's products load —
// so an auto value has to be revisited when the market it assumed turns out
// to be wrong. Without this, the first guess was written to storage and every
// later pass took the early return, which is how 18k US accounts ended up
// holding "metric" while app_market said "us".
const STORAGE_KEY_AUTO_MARKET = "preferred_measure_unit_auto_market";

const safeGet = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const safeSet = (key: string, value: string): void => {
  try { localStorage.setItem(key, value); } catch { /* noop */ }
};

/**
 * Default measurement unit for a given market.
 * US users expect cups/oz/tsp/tbsp; everyone else gets metric.
 */
function defaultUnitForMarket(market: string): MeasureUnit {
  return market === "us" ? "cups" : "metric";
}

/**
 * Which unit to show, given what is in storage and the market we now believe
 * the reader is in. Pure so the precedence can be tested on its own — it is
 * the part that went wrong for two thirds of the US customer base.
 */
export function resolveUnit(input: {
  stored: string | null;
  userSet: boolean;
  autoMarket: string | null;
  market: string;
}): MeasureUnit {
  const { stored, userSet, autoMarket, market } = input;
  const isValid = stored === "metric" || stored === "cups" || stored === "ounces";
  if (isValid && userSet) return stored as MeasureUnit;
  if (isValid && autoMarket === market) return stored as MeasureUnit;
  return defaultUnitForMarket(market);
}

/**
 * Hook to persist the user's preferred measurement unit.
 * - localStorage for guests, DB for logged-in users.
 * - First-time auto-default: US market → "cups", everywhere else → "metric".
 * - Once the user picks a unit explicitly, the auto-default never overrides it.
 */
export function usePreferredMeasureUnit() {
  const { user } = useAuth();
  const { market } = useLocale();
  const [unit, setUnitState] = useState<MeasureUnit>(() => {
    // A stored value is only trusted on sight when the user chose it. An auto
    // value is re-derived against the market we now believe in.
    return resolveUnit({
      stored: safeGet(STORAGE_KEY),
      userSet: safeGet(STORAGE_KEY_USER_SET) === "true",
      autoMarket: safeGet(STORAGE_KEY_AUTO_MARKET),
      market,
    });
  });
  const [loaded, setLoaded] = useState(false);

  // Load from DB on login
  useEffect(() => {
    if (!user?.id) {
      setLoaded(true);
      return;
    }
    supabase
      .from("app_users")
      .select("preferred_measure_unit")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.preferred_measure_unit) {
          const dbUnit = data.preferred_measure_unit as MeasureUnit;
          setUnitState(dbUnit);
          safeSet(STORAGE_KEY, dbUnit);
          safeSet(STORAGE_KEY_USER_SET, "true");
        }
        setLoaded(true);
      });
  }, [user?.id]);

  // Keep the auto default in step with the market for as long as the user has
  // not chosen for themselves. This used to run once and then bail out on the
  // presence of any stored value, which froze whatever the very first render
  // guessed — before login, before the user's products said which market they
  // are actually in.
  //
  // Nothing here writes to app_users: that column records a deliberate choice,
  // and filling it with a guess is what made 17,659 auto-assigned "metric"
  // rows indistinguishable from the 312 people who really did pick metric.
  useEffect(() => {
    if (!loaded) return;
    if (safeGet(STORAGE_KEY_USER_SET) === "true") return;
    if (safeGet(STORAGE_KEY_AUTO_MARKET) === market && safeGet(STORAGE_KEY)) return;
    const auto = defaultUnitForMarket(market);
    setUnitState(auto);
    safeSet(STORAGE_KEY, auto);
    safeSet(STORAGE_KEY_AUTO_MARKET, market);
  }, [market, loaded]);

  const setUnit = useCallback(
    (newUnit: MeasureUnit) => {
      setUnitState(newUnit);
      safeSet(STORAGE_KEY, newUnit);
      safeSet(STORAGE_KEY_USER_SET, "true");
      if (user?.id) {
        supabase
          .from("app_users")
          .update({ preferred_measure_unit: newUnit } as any)
          .eq("id", user.id)
          .then(() => {});
      }
    },
    [user?.id],
  );

  return { unit, setUnit, loaded };
}
